import {
    ApolloClient,
    from,
    HttpLink,
    InMemoryCache,
    NormalizedCacheObject
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { IncomingHttpHeaders } from 'http';
import { getPropertyKey } from './typePolicies';
import { setupLogger } from '@zoopla/logger';

let apolloClientInstance: ApolloClient<NormalizedCacheObject> | null = null;

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const PREFIX = 'GraphQL';

    let messages: string[] = [];

    if (graphQLErrors) {
        messages = messages.concat(
            graphQLErrors
                .filter(
                    ({ path, message }) =>
                        // dont log known listings validation errors
                        !(
                            operation.operationName === 'getListingData' &&
                            path &&
                            path[0].toString() === 'searchResults' &&
                            message.startsWith('422:')
                        ) &&
                        // dont log expired listing errors
                        !(
                            operation.operationName === 'getListingDetails' &&
                            path &&
                            path[0].toString() === 'listing' &&
                            message === 'Listing Expired'
                        ) &&
                        !(
                            operation.operationName === 'housePricesResults' &&
                            path &&
                            path[0].toString() === 'propertiesSearch' &&
                            message === 'UPRN is required'
                        )
                )
                .map(
                    ({ message, path }) =>
                        `[${PREFIX}] [graphQLError]: Operation: ${operation.operationName}, Message: ${message}, Path: ${path}`
                )
        );
    }

    if (networkError) {
        messages.push(
            `[${PREFIX}] [networkError]: Operation: ${operation.operationName}, Message: ${networkError}`
        );
    }

    const logger = setupLogger({
        component: 'apollo-client',
        custodian: 'platform'
    });

    messages.forEach((message) => {
        if (process.env.NODE_ENV === 'test') {
            /* When running in test mode it is possible for the error link to be
            called after the test has finished and been torn down. This tends
            to happen because we do not always adopt an AbortController.abort()
            on unmount pattern, mainly because Apollo does not support it, or
            that its "skip" alternative is not always reliable. Another fix is
            to correctly waitFor all state updates to complete before ending
            the test. Essentially this problem does not exist when handling a
            component unmount/teardown gracefully, i.e when using the preferred
            "fetchGraphql" method with an abort.
            However one specific problem comes into play with msw and jsdom.
            Whereby the msw fetch interceptor will check window.location as part
            of its request handling logic, but if this request happens
            after the test and jsdom has been torn down then window.location
            will not exist and the following error will occur:
            TypeError: Cannot read properties of null (reading '_location')
            Which will trigger this error link and logging logic, which will
            cause jest to error because we "Cannot log after tests are done".
            This problem can sometimes behave like a race condition and only
            occur on slower systems, such as when running in CI.
            Because our ambition is to eventually remove Apollo and because
            gracefully aborting a request on unmount is not always possible with
            Apollo and its hooks, it feels prudent to simply ignore this very
            specific test error so that our tests are more reliable/pure.
            */
            if (message.includes('_location')) {
                return;
            }
            logger.info(message);
            return;
        }
        logger.error(message);
    });
});

const createApolloClient = (
    initialState = {},
    headers?: IncomingHttpHeaders
) => {
    const uri = process.env['NEXT_PUBLIC_GRAPHQL_API'];
    if (!uri) {
        throw new Error('Missing env var: NEXT_PUBLIC_GRAPHQL_API');
    }

    const apiKey =
        process.env['PORT'] === '3001'
            ? 'mock'
            : process.env['NEXT_PUBLIC_API_KEY'];
    if (!apiKey) {
        throw new Error('Missing env var: NEXT_PUBLIC_API_KEY');
    }

    const httpLink = new HttpLink({
        uri,
        credentials: 'include',
        fetch,
        headers: {
            'X-Api-Key': apiKey,
            cookie: headers?.cookie || ''
        }
    });

    return new ApolloClient({
        link: from([errorLink, httpLink]),
        ssrMode: typeof window === 'undefined',
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        user: {
                            merge: true
                        },
                        property: {
                            merge: true
                        }
                    }
                },
                User: {
                    keyFields: ['userId'],
                    merge: true
                },
                Property: {
                    keyFields: getPropertyKey
                },
                ListingData: {
                    merge: true
                }
            }
        }).restore(initialState)
    });
};

const getApolloClient = (
    initialState?: null | NormalizedCacheObject,
    headers?: IncomingHttpHeaders
) => {
    // Reuse client on the client-side if available
    if (!apolloClientInstance) {
        apolloClientInstance = createApolloClient(initialState ?? {}, headers);
    }

    return apolloClientInstance;
};

const initializeApollo = (
    initialState?: null | NormalizedCacheObject,
    headers?: IncomingHttpHeaders
) => {
    const isBrowser = typeof window !== 'undefined';
    /** try to re-use client if in browser */
    return isBrowser
        ? getApolloClient(initialState, headers)
        : createApolloClient(initialState ?? {}, headers);
};

/**
 * Unlike getApolloClient, this function will only return the client if it has
 * already been created. This is useful for testing purposes.
 */
const getApollo = () => apolloClientInstance;

export {
    createApolloClient,
    getApolloClient,
    apolloClientInstance,
    initializeApollo,
    getApollo
};
